import React, { createContext, useReducer, useContext, useEffect, useCallback, useRef } from 'react';
import { generateClient } from "aws-amplify/api";
import { listLessons } from "../graphql/queries"; // Adjusted import path
import { 
  deduplicateUserLessons, 
  selectBestUserLesson, 
  getBestUserLesson,
  createBestUserLessonMap,
  findOrCreateUserLesson,
  ensureUserLessonsLoaded,
  getUserLessonByUserAndLesson,
  invalidateUserLessonCache
} from "../utils/userLessonUtils";

const LessonContext = createContext();

const initialState = {
  userAttributes: null,
  lesson: null,
  userLessons: [],
  userLesson: null,
  nextLessonID: null,
  previousLessonID: null,
  lessonLoading: true,
  userLessonLoading: true,
  lessonError: false,
  userLessonError: false,
  lessons: [],
  isUpdatingUserLessons: false,
  hasLoadedUserLessons: false,
  isFetchingUserLessons: false,
  isInitialUserLessonsLoad: true, // Flag for first-time load
};

const lessonReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER_ATTRIBUTES':
      return { ...state, userAttributes: action.payload };
    case 'SET_LESSON':
      return { ...state, lesson: action.payload, lessonLoading: false };
    case 'SET_USER_LESSONS':
      // Ensure we don't have duplicates when setting user lessons
      const deduplicatedLessons = deduplicateUserLessons(action.payload);
      return { 
        ...state, 
        userLessons: deduplicatedLessons, 
        userLessonLoading: false, 
        hasLoadedUserLessons: true,
        isFetchingUserLessons: false,
        isInitialUserLessonsLoad: false
      };
    case 'SET_USER_LESSON':
      return { ...state, userLesson: action.payload, userLessonLoading: false };
    case 'SET_NEXT_LESSON_ID':
      return { ...state, nextLessonID: action.payload };
    case 'SET_PREVIOUS_LESSON_ID':
      return { ...state, previousLessonID: action.payload };
    case 'SET_LESSON_LOADING':
      return { ...state, lessonLoading: action.payload };
    case 'SET_USER_LESSON_LOADING':
      return { ...state, userLessonLoading: action.payload };
    case 'SET_LESSON_ERROR':
      return { ...state, lessonError: action.payload, lessonLoading: false };
    case 'SET_USER_LESSON_ERROR':
      return { ...state, userLessonError: action.payload, userLessonLoading: false, isFetchingUserLessons: false };
    case 'SET_LESSONS':
      return { ...state, lessons: action.payload };
    case 'UPDATE_USER_LESSON':
      const updatedUserLessons = state.userLessons.map(ul =>
        ul.id === action.payload.id ? action.payload : ul
      );
      // Ensure deduplication after update using canonical comparator
      const deduplicatedUpdatedLessons = deduplicateUserLessons(updatedUserLessons);
      
      // Also update the current userLesson if it matches
      const updatedUserLesson = state.userLesson && state.userLesson.id === action.payload.id 
        ? action.payload 
        : state.userLesson;
      return {
        ...state,
        userLessons: deduplicatedUpdatedLessons,
        userLesson: updatedUserLesson,
      };
    case 'SET_IS_UPDATING_USER_LESSONS':
      return { ...state, isUpdatingUserLessons: action.payload };
    case 'SET_IS_FETCHING_USER_LESSONS':
      return { ...state, isFetchingUserLessons: action.payload };
    case 'CLEAR_UPDATE_FLAG':
      return { ...state, isUpdatingUserLessons: false };
    case 'RESET_USER_LESSONS_LOADED':
      return { ...state, hasLoadedUserLessons: false, isInitialUserLessonsLoad: true };
    case 'ADD_USER_LESSON':
      // Add a new user lesson, ensuring deduplication using canonical comparator
      const newUserLessons = [...state.userLessons, action.payload];
      const deduplicatedNewLessons = deduplicateUserLessons(newUserLessons);
      return {
        ...state,
        userLessons: deduplicatedNewLessons,
      };
    default:
      return state;
  }
};

export const LessonProvider = ({ children }) => {
  const [state, dispatch] = useReducer(lessonReducer, initialState);
  const isFetchingRef = useRef(false);
  const hasLoadedRef = useRef(false);

  // Centralized function to fetch all user lessons for a given user using cached loaders
  const fetchAllUserLessons = useCallback(async (userID, force = false) => {
    // Prevent duplicate simultaneous fetches
    if (isFetchingRef.current && !force) {
      console.log('User lessons fetch already in progress, skipping...');
      return;
    }

    // If already loaded and not forcing, skip
    if (hasLoadedRef.current && !force) {
      console.log('User lessons already loaded, skipping...');
      return;
    }

    if (!userID) {
      console.log('No userID provided, cannot fetch user lessons');
      return;
    }

    isFetchingRef.current = true;
    dispatch({ type: 'SET_USER_LESSON_ERROR', payload: false });
    dispatch({ type: 'SET_IS_FETCHING_USER_LESSONS', payload: true });

    try {
      // Use cached loader that implements memoized promises and large page sizes
      const snapshot = await ensureUserLessonsLoaded(userID, force);
      console.log(`Loaded ${snapshot.items.length} user lessons for user ${userID} (cached)`);
      
      dispatch({ type: 'SET_USER_LESSONS', payload: snapshot.items });
      dispatch({ type: 'CLEAR_UPDATE_FLAG' });
      hasLoadedRef.current = true;
    } catch (error) {
      console.error("Error fetching user lessons:", error);
      dispatch({ type: 'SET_USER_LESSON_ERROR', payload: true });
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Enhanced ensureUserLessonsLoaded that also updates context state
  const ensureUserLessonsLoadedWithContext = useCallback(async (userID, force = false) => {
    if (!userID) {
      throw new Error('userID is required');
    }

    // If we haven't started loading yet, dispatch the loading state
    if (!isFetchingRef.current && !hasLoadedRef.current) {
      dispatch({ type: 'SET_IS_FETCHING_USER_LESSONS', payload: true });
      isFetchingRef.current = true;
    }

    try {
      // Use the utility directly 
      const snapshot = await ensureUserLessonsLoaded(userID, force);
      
      // Update context state with the results
      if (!hasLoadedRef.current || force) {
        console.log(`Context: updating state with ${snapshot.items.length} user lessons for user ${userID}`);
        dispatch({ type: 'SET_USER_LESSONS', payload: snapshot.items });
        hasLoadedRef.current = true;
      }
      
      return snapshot;
    } catch (error) {
      console.error("Error in ensureUserLessonsLoadedWithContext:", error);
      dispatch({ type: 'SET_USER_LESSON_ERROR', payload: true });
      throw error;
    } finally {
      if (isFetchingRef.current) {
        isFetchingRef.current = false;
      }
    }
  }, []);

  // Function to invalidate cache and force refresh
  const refreshUserLessons = useCallback(async (userID) => {
    if (userID) {
      invalidateUserLessonCache(userID);
      hasLoadedRef.current = false;
      dispatch({ type: 'SET_USER_LESSON_ERROR', payload: false });
      await fetchAllUserLessons(userID, true);
    }
  }, [fetchAllUserLessons]);

  useEffect(() => {
    const fetchLessons = async () => {
      const client = generateClient();
      try {
        const result = await client.graphql({
          query: listLessons,
          variables: {
            limit: 100,
          },
        });
        const fetchedLessons = result.data.listLessons.items;
        // Order lessons by the 'order' attribute
        const orderedLessons = fetchedLessons.sort((a, b) => a.order - b.order);
        dispatch({ type: 'SET_LESSONS', payload: orderedLessons });
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }
    };

    fetchLessons();
  }, []);

  // Expose the fetch function and utilities through context
  const contextValue = {
    state,
    dispatch,
    fetchAllUserLessons,
    refreshUserLessons,
    // Export utilities for use by components
    selectBestUserLesson,
    deduplicateUserLessons,
    getBestUserLesson,
    createBestUserLessonMap,
    findOrCreateUserLesson,
    ensureUserLessonsLoaded: ensureUserLessonsLoadedWithContext,
    getUserLessonByUserAndLesson,
    invalidateUserLessonCache,
  };

  return (
    <LessonContext.Provider value={contextValue}>
      {children}
    </LessonContext.Provider>
  );
};

export const useLessonContext = () => useContext(LessonContext);
